import React from 'react';
import { Box, Typography, Stack, Grid, Button, Chip, Switch, Skeleton, Dialog, DialogContent, DialogActions, CircularProgress } from '@mui/material';
import InboxRoundedIcon from '@mui/icons-material/InboxRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import PauseCircleFilledRoundedIcon from '@mui/icons-material/PauseCircleFilledRounded';
import { PRIMARY, PRIMARY_LIGHT, PRIMARY_DARK, PRIMARY_BORDER } from '../theme';
import { fileKind, fmtSize } from '../utils/fileStore';
import brandIcon from '../images/Fav-Icon.png';

export const card = { bgcolor: '#fff', border: '1px solid #E6EAF1', borderRadius: '7px', boxShadow: '0 1px 3px rgba(16,24,40,0.06)' };
export const solidBtn = { bgcolor: PRIMARY, color: '#fff', fontWeight: 700, borderRadius: '7px', boxShadow: 'none', textTransform: 'none', '&:hover': { bgcolor: PRIMARY_DARK, boxShadow: 'none' } };
export const tonalBtn = { bgcolor: PRIMARY_LIGHT, color: PRIMARY, border: `1px solid ${PRIMARY_BORDER}`, fontWeight: 700, borderRadius: '7px', boxShadow: 'none', textTransform: 'none', '&:hover': { bgcolor: '#E7DFFC' } };
export const ghostBtn = { color: '#475569', bgcolor: '#fff', border: '1px solid #E5E7EB', fontWeight: 700, borderRadius: '7px', textTransform: 'none', '&:hover': { bgcolor: '#F8FAFC' } };
export const successBtn = { bgcolor: '#16A34A', color: '#fff', fontWeight: 700, borderRadius: '7px', boxShadow: 'none', textTransform: 'none', '&:hover': { bgcolor: '#15803D' } };
export const dangerBtn = { color: '#E11D48', bgcolor: '#fff', border: '1px solid #FBCFE8', fontWeight: 700, borderRadius: '7px', textTransform: 'none', '&:hover': { bgcolor: '#FEF2F2' } };
export const field = { '& .MuiOutlinedInput-root': { borderRadius: '7px', fontSize: 14, bgcolor: '#F8FAFC', '& fieldset': { borderColor: '#E5E7EB' }, '&.Mui-focused fieldset': { borderColor: PRIMARY, borderWidth: 1.5 } } };

export const th = { textAlign: 'left', px: 2, py: 1.15, fontSize: 10.5, fontWeight: 700, color: '#6E6B99', letterSpacing: 0.6, borderBottom: '1px solid #E8E6F3', whiteSpace: 'nowrap' };
export const td = { px: 2, py: 1.15, borderBottom: '1px solid #EEF0F6' };

/**
 * Page-level actions. The title/subtitle banner that used to live here has been
 * removed from every screen (the sidebar and breadcrumb already name the page),
 * so this now renders only its `children` — the page actions — as a right-aligned
 * row, and nothing at all when a page has none. `title`, `subtitle` and `icon`
 * are still accepted so existing call sites keep working.
 */
// eslint-disable-next-line no-unused-vars
export function PageHeader({ title, subtitle, icon, children, sx }) {
    if (!React.Children.toArray(children).length) return null;
    return (
        <Stack direction="row" sx={{ justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1, mb: 1.5, ...sx }}>
            {children}
        </Stack>
    );
}

// A figure that hasn't arrived yet reads better as a shimmering placeholder the
// size of the number than as a dash, which looks like a real "no value" answer.
export function StatCards({ items }) {
    return (
        <Grid container spacing={1.2} sx={{ mb: 1.2 }}>
            {items.map((k) => (
                <Grid size={{ xs: 12, sm: 6, lg: 12 / Math.min(items.length, 5) }} key={k.label}>
                    <Box sx={{
                        ...card, p: 1.6, height: '100%',
                        position: 'relative', overflow: 'hidden',
                        borderRadius: '10px',
                        bgcolor: k.bg,
                        border: `1px solid ${k.color}26`,
                        transition: 'transform .18s ease, box-shadow .18s ease',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 12px 26px -16px ${k.color}`,
                        },
                        '&:hover .stat-watermark': { transform: 'rotate(-8deg) scale(1.06)', opacity: 0.20 },
                    }}>
                        {/* The card's own icon as a corner watermark — always relevant
                            to the metric, and it follows if the icon ever changes.
                            Anchored past the bottom-right corner so it never sits
                            behind the icon tile or the numbers. */}
                        <k.icon
                            aria-hidden
                            className="stat-watermark"
                            sx={{
                                position: 'absolute', right: -12, bottom: -26, fontSize: 84,
                                color: k.color, opacity: 0.13, pointerEvents: 'none',
                                transform: 'rotate(-10deg)',
                                transition: 'transform .25s ease, opacity .25s ease',
                            }}
                        />
                        {/* A little light off the top-left so the tint isn't flat. */}
                        <Box
                            aria-hidden
                            sx={{
                                position: 'absolute', inset: 0, pointerEvents: 'none',
                                background: 'linear-gradient(135deg, #FFFFFF8C 0%, #FFFFFF00 48%)',
                            }}
                        />
                        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative' }}>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography sx={{ fontSize: 10, fontWeight: 700, color: k.color, textTransform: 'uppercase', letterSpacing: 0.4 }}>{k.label}</Typography>
                                {k.loading ? (
                                    <Skeleton
                                        variant="rounded" animation="wave" width={58} height={22}
                                        sx={{ mt: 0.5, mb: 0.2, borderRadius: '6px', bgcolor: `${k.color}24` }}
                                    />
                                ) : (
                                    <Typography sx={{ fontSize: 21, fontWeight: 800, color: '#0F172A', mt: 0.3 }}>{k.value}</Typography>
                                )}
                                {k.loading
                                    ? <Skeleton variant="text" animation="wave" width="70%" height={12} sx={{ bgcolor: `${k.color}1C` }} />
                                    : <Typography sx={{ fontSize: 10, color: '#6B7280', mt: 0.1 }}>{k.sub}</Typography>}
                            </Box>
                            <Box sx={{
                                width: 36, height: 36, borderRadius: '9px', flexShrink: 0,
                                bgcolor: '#fff', border: `1px solid ${k.color}1F`,
                                boxShadow: `0 2px 8px -3px ${k.color}59`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <k.icon sx={{ color: k.color, fontSize: 18 }} />
                            </Box>
                        </Stack>
                    </Box>
                </Grid>
            ))}
        </Grid>
    );
}

export function Panel({ title, icon: Icon, chip, chipColor = PRIMARY, chipBg = PRIMARY_LIGHT, hint, action, children, sx }) {
    return (
        <Box sx={{ ...card, p: 0, overflow: 'hidden', ...sx }}>
            <Stack direction="row" sx={{ alignItems: 'center', gap: 1.2, p: 1.6, bgcolor: '#F7F6FD', borderBottom: '1px solid #EAE7F7', flexWrap: 'wrap' }}>
                {Icon && (
                    <Box sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: '#fff', boxShadow: '0 1px 4px rgba(16,24,40,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon sx={{ color: PRIMARY, fontSize: 16 }} />
                    </Box>
                )}
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0F172A' }}>{title}</Typography>
                {chip && <Chip label={chip} size="small" sx={{ bgcolor: chipBg, color: chipColor, fontWeight: 700, fontSize: 11.5 }} />}
                {hint && <Typography sx={{ fontSize: 11.5, color: '#98A0AE', ml: 'auto', display: { xs: 'none', sm: 'block' } }}>{hint}</Typography>}
                {action && <Box sx={{ ml: hint ? 1 : 'auto' }}>{action}</Box>}
            </Stack>
            {children}
        </Box>
    );
}

/**
 * Placeholder cards shaped like the content that is loading, so the panel keeps
 * its height and the page doesn't jump when the real rows arrive.
 */
export function SkeletonCards({ count = 6, height = 168, size = { xs: 12, md: 6, xl: 4 }, p = 1.8 }) {
    return (
        <Box sx={{ p }}>
            <Grid container spacing={1.5}>
                {Array.from({ length: count }).map((_, i) => (
                    <Grid size={size} key={i}>
                        <Skeleton
                            variant="rounded" animation="wave" height={height}
                            sx={{ borderRadius: '9px', bgcolor: '#F1EFFA' }}
                        />
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
}

export function SkeletonRows({ count = 5, height = 56, p = 1.5 }) {
    return (
        <Stack spacing={1} sx={{ p }}>
            {Array.from({ length: count }).map((_, i) => (
                <Skeleton
                    key={i} variant="rounded" animation="wave" height={height}
                    sx={{ borderRadius: '9px', bgcolor: '#F1EFFA' }}
                />
            ))}
        </Stack>
    );
}

/** A full-panel loading state: the brand mark centered inside a spinning
 * ring, in place of a bare CircularProgress — used where a loader takes up
 * enough of the screen (an empty chat pane, a full page) that a plain spinner
 * reads as generic/unbranded. */
export function BrandLoader({ size = 56, thickness = 2.6 }) {
    const iconSize = size * 0.5;
    return (
        <Box sx={{ position: 'relative', width: size, height: size }}>
            <CircularProgress
                size={size}
                thickness={thickness}
                sx={{ color: PRIMARY_BORDER, position: 'absolute', inset: 0 }}
            />
            <CircularProgress
                size={size}
                thickness={thickness}
                variant="indeterminate"
                sx={{ color: PRIMARY, position: 'absolute', inset: 0, animationDuration: '1.1s' }}
            />
            <Box
                component="img"
                src={brandIcon}
                alt=""
                sx={{
                    position: 'absolute', top: '50%', left: '50%',
                    width: iconSize, height: iconSize, transform: 'translate(-50%, -50%)',
                }}
            />
        </Box>
    );
}

export function EmptyState({ icon: Icon, title, hint }) {
    return (
        <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
            <Icon sx={{ fontSize: 36, color: '#CBD2DD' }} />
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#64748B', mt: 1 }}>{title}</Typography>
            {hint && <Typography sx={{ fontSize: 12.5, color: '#98A0AE', mt: 0.4 }}>{hint}</Typography>}
        </Box>
    );
}

// A failed load reads as an empty screen, not a red error: same slot, calm wording,
// and the page's own Retry button (passed as `action`) is kept underneath.
const TECHNICAL = /cannot reach|took too long|something went wrong|network|timeout|failed|could not load|unable/i;
export function LoadAlert({ children, action, sx }) {
    const text = typeof children === 'string' ? children : '';
    const hint = text && !TECHNICAL.test(text) ? text : '';
    return (
        <Box sx={{ textAlign: 'center', py: 5, px: 2, ...sx }}>
            <InboxRoundedIcon sx={{ fontSize: 38, color: '#CBD2DD' }} />
            <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: '#64748B', mt: 1 }}>No data found</Typography>
            <Typography sx={{ fontSize: 12.5, color: '#98A0AE', mt: 0.4 }}>{hint || 'There is nothing to show here right now.'}</Typography>
            {action && <Box sx={{ mt: 1.6 }}>{action}</Box>}
        </Box>
    );
}

const STATUS = {
    pending: { label: 'Pending', color: '#B45309', bg: '#FFF7ED' },
    submitted: { label: 'Awaiting review', color: '#0369A1', bg: '#E0F2FE' },
    approved: { label: 'Approved', color: '#16A34A', bg: '#DCFCE7' },
    rejected: { label: 'Rejected', color: '#E11D48', bg: '#FEE2E2' },
    open: { label: 'Open', color: PRIMARY, bg: PRIMARY_LIGHT },
    closed: { label: 'Closed', color: '#64748B', bg: '#F1F5F9' },
    cancelled: { label: 'Cancelled', color: '#64748B', bg: '#F1F5F9' },
    withdrawn: { label: 'Withdrawn', color: '#64748B', bg: '#F1F5F9' },
    completed: { label: 'Completed', color: '#16A34A', bg: '#DCFCE7' },
    Active: { label: 'Active', color: '#16A34A', bg: '#DCFCE7' },
    Inactive: { label: 'Inactive', color: '#64748B', bg: '#F1F5F9' },
};

export function StatusChip({ status, size = 'small' }) {
    // Statuses arrive lowercased from the API. Falling through with the raw value
    // printed a lowercase "closed" next to a capitalised "Open".
    const raw = String(status ?? '');
    const s = STATUS[status] || { label: raw.charAt(0).toUpperCase() + raw.slice(1), color: '#64748B', bg: '#F1F5F9' };
    return <Chip label={s.label} size={size} sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: s.bg, color: s.color }} />;
}

export function StatusToggle({ value, onChange, activeHint = 'Offered in the employee form', inactiveHint = 'Kept on record, but no longer offered' }) {
    const on = value === 'Active';
    const toggle = () => onChange(on ? 'Inactive' : 'Active');
    const color = on ? '#16A34A' : '#94A3B8';

    return (
        <Stack
            direction="row"
            onClick={toggle}
            role="switch"
            aria-checked={on}
            sx={{
                alignItems: 'center', gap: 1.4, px: 1.6, py: 1.3, cursor: 'pointer',
                borderRadius: '9px',
                border: `1.5px solid ${on ? '#BBF7D0' : '#E5E7EB'}`,
                bgcolor: on ? '#F0FDF4' : '#F8FAFC',
                transition: 'background-color .18s, border-color .18s',
                '&:hover': { borderColor: on ? '#86EFAC' : '#D8DEE9' },
            }}
        >
            <Box sx={{ width: 34, height: 34, borderRadius: '9px', bgcolor: '#fff', border: `1px solid ${on ? '#BBF7D0' : '#E5E7EB'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {on
                    ? <CheckCircleRoundedIcon sx={{ fontSize: 19, color }} />
                    : <PauseCircleFilledRoundedIcon sx={{ fontSize: 19, color }} />}
            </Box>

            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: on ? '#166534' : '#475569' }}>
                    {on ? 'Active' : 'Inactive'}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: on ? '#3F8B5F' : '#98A0AE' }}>
                    {on ? activeHint : inactiveHint}
                </Typography>
            </Box>

            <Switch
                checked={on}
                onChange={toggle}
                onClick={(e) => e.stopPropagation()}
                sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#16A34A', '&:hover': { bgcolor: 'rgba(22,163,74,0.08)' } },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#16A34A', opacity: 1 },
                    '& .MuiSwitch-track': { bgcolor: '#CBD2DD', opacity: 1 },
                }}
            />
        </Stack>
    );
}

/**
 * `title` overrides the bold line while `fileName` still drives the type badge —
 * for lists where what the document *is* ("PAN Card") identifies the row better
 * than what the file happens to be called ("Screenshot_2024-10-26.png").
 */
export function FileBadge({ fileName, fileSize, sub, title }) {
    const kind = fileKind(fileName);
    const primary = title || fileName;
    return (
        <Stack direction="row" spacing={1.2} sx={{ alignItems: 'center', minWidth: 0 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: kind.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: kind.color, letterSpacing: 0.3 }}>{kind.label}</Typography>
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }} noWrap>{primary || '—'}</Typography>
                <Typography sx={{ fontSize: 11, color: '#98A0AE' }} noWrap>{sub || fmtSize(fileSize)}</Typography>
            </Box>
        </Stack>
    );
}

export function ConfirmDialog({ open, onClose, onConfirm, title, body, confirmLabel = 'Remove' }) {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
            <DialogContent sx={{ pt: 3.5, pb: 2, textAlign: 'center' }}>
                <Box sx={{ width: 60, height: 60, borderRadius: '50%', bgcolor: '#FEF2F2', border: '4px solid #FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.8 }}>
                    <WarningAmberRoundedIcon sx={{ fontSize: 30, color: '#DC2626' }} />
                </Box>
                <Typography sx={{ fontSize: 17, fontWeight: 800, color: '#111827', mb: 0.8 }}>{title}</Typography>
                <Typography sx={{ fontSize: 12.5, color: '#6B7280', px: 1.5, lineHeight: 1.5 }}>{body}</Typography>
            </DialogContent>
            <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1 }}>
                <Button fullWidth onClick={onClose} sx={{ ...ghostBtn, height: 40, fontSize: 13 }}>Cancel</Button>
                <Button fullWidth onClick={onConfirm} sx={{ height: 40, fontSize: 13, fontWeight: 700, textTransform: 'none', borderRadius: '7px', bgcolor: '#DC2626', color: '#fff', boxShadow: 'none', '&:hover': { bgcolor: '#B91C1C' } }}>{confirmLabel}</Button>
            </DialogActions>
        </Dialog>
    );
}

export { fmtSize };
